/**
 * DOCTRACK part 4 (design 5.1-5.4) - what changed between two versions of a document, on screen.
 *
 * - The dialog shows the KEPT comparison (a GET never works one out); "Compare with the earlier version", Yes, Pick
 *   another, "Mark checked" and "Read again" are his taps (the routes refuse anyone but an admin).
 * - A guessed family asks first ("compared with X (guessed) - right?") with no count and no changes until he answers.
 * - It never says "no changes" unless every sentence was compared: a failed load says "Could not load the comparison."
 * - The app's own letter is never read again (its kept terms are the letter's own values).
 */
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type { ChangeSummary, FilingChanges, TermChange, TermFlag } from '../../types';
import { Dialog } from '../ui';

const LINK = 'text-sm font-medium text-red-600 hover:underline disabled:opacity-50';

/**
 * Said after any tap that changes a comparison (Yes, Pick another, Compare, Mark checked, Read again), so every row list
 * on the page reads its words again - after Pick another a row never keeps the old pair's count (the one review, I8).
 */
export const DOCUMENT_CHANGES_EVENT = 'apex-document-changes';
const announceChanges = (): void => {
  try { window.dispatchEvent(new Event(DOCUMENT_CHANGES_EVENT)); } catch { /* nothing to tell */ }
};

/** The compared rows' one-line summaries, by file link id. No call for no rows; a failure is said, never thrown. */
export function useChangeSummaries(fileLinkIds: string[]): { byLink: Record<string, ChangeSummary>; failed: boolean } {
  const key = fileLinkIds.join(',');
  const [state, setState] = useState<{ byLink: Record<string, ChangeSummary>; failed: boolean }>({ byLink: {}, failed: false });
  const [heard, setHeard] = useState(0);
  useEffect(() => {
    const again = () => setHeard((n) => n + 1);
    window.addEventListener(DOCUMENT_CHANGES_EVENT, again);
    return () => window.removeEventListener(DOCUMENT_CHANGES_EVENT, again);
  }, []);
  useEffect(() => {
    let live = true;
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) { setState({ byLink: {}, failed: false }); return () => { live = false; }; }
    // Promise.resolve first: a call that throws before it returns a promise is a failed check, said - never a crash.
    Promise.resolve().then(() => api.documentChangeSummaries(ids))
      .then((list) => { if (live) setState({ byLink: Object.fromEntries(list.map((s) => [s.fileLinkId, s])), failed: false }); })
      .catch(() => { if (live) setState({ byLink: {}, failed: true }); });
    return () => { live = false; };
  }, [key, heard]);
  return state;
}

/** "2 changes" (or the question, or "Compare with the earlier version") on a document's row. */
export function ChangesButton({ summary, onOpen }: { summary: ChangeSummary; onOpen: (filingId: string) => void }) {
  return (
    <button type="button" className="text-xs font-medium text-red-600 hover:underline" onClick={() => onOpen(summary.filingId)}>
      {summary.words}
    </button>
  );
}

const termLine = (t: TermChange) => `${t.what} - ${t.label}`;
const flagLine = (f: TermFlag) => `${f.words}${f.side === 'older' ? ' (the earlier version)' : ''}`;

export default function DocumentChangesDialog({ filingId, onClose, onOpenDocument }: {
  filingId: string;
  onClose: () => void;
  onOpenDocument?: (fileLinkId: string, title: string) => void;
}) {
  const [answer, setAnswer] = useState<FilingChanges | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [pickId, setPickId] = useState('');

  useEffect(() => {
    let live = true;
    setAnswer(null); setLoadFailed(false); setProblem(null); setPicking(false);
    api.documentChanges(filingId)
      .then((a) => { if (live) setAnswer(a); })
      .catch(() => { if (live) setLoadFailed(true); });
    return () => { live = false; };
  }, [filingId]);

  const tap = async (run: () => Promise<FilingChanges>, failWords: (e: unknown) => string) => {
    setBusy(true); setProblem(null);
    try {
      setAnswer(await run());
      setPicking(false);
      announceChanges();
    } catch (e) {
      setProblem(failWords(e));
    } finally {
      setBusy(false);
    }
  };
  const failed = (words: string) => () => words;
  const view = answer?.view ?? null;

  return (
    <Dialog open onClose={onClose} title={answer?.title ?? 'Document'} size="lg">
      <div className="space-y-4 text-sm">
        {answer && (
          <div className="flex flex-wrap items-center gap-3">
            {answer.notChecked && <span className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-800">not checked yet</span>}
            {answer.filingState === 'not_checked' && (
              <button type="button" className={LINK} disabled={busy} onClick={() => tap(() => api.markDocumentChecked(filingId), failed('Could not mark it checked.'))}>Mark checked</button>
            )}
            {answer.source === 'read' && (
              <button type="button" className={LINK} disabled={busy}
                onClick={() => tap(() => api.readDocumentAgain(filingId), (e) => `Could not read it again.${e instanceof Error && e.message ? ` ${e.message}` : ''}`)}>
                Read again
              </button>
            )}
            {onOpenDocument && (
              <button type="button" className={LINK} onClick={() => onOpenDocument(answer.fileLinkId, answer.title)}>Open the document</button>
            )}
          </div>
        )}
        {problem && <p role="alert" className="text-red-600">{problem}</p>}
        {loadFailed && <p className="text-gray-600">Could not load the comparison.</p>}
        {answer?.state === 'first_version' && <p className="text-gray-600">First version on file - nothing to compare with yet.</p>}
        {answer?.state === 'not_compared' && (
          <button type="button" className={LINK} disabled={busy}
            onClick={() => tap(() => api.compareDocument(filingId), failed('Could not load the comparison.'))}>
            Compare with the earlier version
          </button>
        )}
        {view && view.guessed && (
          <div className="space-y-2">
            <p className="font-medium text-gray-900">{view.headline}</p>
            <div className="flex gap-3">
              <button type="button" className={LINK} disabled={busy} onClick={() => tap(() => api.confirmDocumentFamily(filingId), failed('Could not load the comparison.'))}>Yes</button>
              <button type="button" className={LINK} disabled={busy} onClick={() => setPicking(true)}>Pick another</button>
            </div>
          </div>
        )}
        {view && !view.guessed && (
          <div className="space-y-3">
            <p className="text-base font-semibold text-gray-900">{view.headline}</p>
            <p className="text-gray-600">{view.comparedWithWords}</p>
            {view.notes.map((n) => <p key={n} className="text-amber-800">{n}</p>)}
            {view.termChanges.length > 0 && (
              <ul className="space-y-2">
                {view.termChanges.map((t, i) => (
                  <li key={`t-${i}`} className="rounded border border-gray-200 p-2">
                    <p className="font-medium text-gray-900">{termLine(t)}</p>
                    {t.before !== null && <p className="text-gray-700">{`before: ${t.before}`}</p>}
                    {t.after !== null && <p className="text-gray-700">{`after: ${t.after}`}</p>}
                    {t.beforeSentence && <p className="text-xs text-gray-500">{`"${t.beforeSentence}"`}</p>}
                    {t.afterSentence && <p className="text-xs text-gray-500">{`"${t.afterSentence}"`}</p>}
                    {t.page !== null && <p className="text-xs text-gray-500">{`page ${t.page}`}</p>}
                    {t.handwritten && <p className="text-xs text-amber-800">handwritten - check it</p>}
                  </li>
                ))}
              </ul>
            )}
            {[...view.disagreements, ...view.onlyInOne].length > 0 && (
              <ul className="space-y-1">
                {view.disagreements.map((f, i) => <li key={`d-${i}`} className="text-amber-800">{`${f.label}: ${flagLine(f)}`}</li>)}
                {view.onlyInOne.map((f, i) => <li key={`o-${i}`} className="text-amber-800">{`${f.label}: ${flagLine(f)}`}</li>)}
              </ul>
            )}
            {view.sentenceChanges.length > 0 && (
              <ul className="space-y-2">
                {view.sentenceChanges.map((s, i) => {
                  const page = s.kind === 'removed' ? s.pageBefore : s.pageAfter;
                  const what = s.kind === 'changed' ? 'Changed' : s.kind === 'added' ? view.addedWords : 'Removed';
                  return (
                    <li key={`s-${i}`} className="rounded border border-gray-200 p-2">
                      <p className="font-medium text-gray-900">{`${what} - page ${page ?? '?'}`}</p>
                      {s.before && <p className="text-xs text-gray-500">{`before: "${s.before}"`}</p>}
                      {s.after && <p className="text-xs text-gray-500">{`after: "${s.after}"`}</p>}
                    </li>
                  );
                })}
              </ul>
            )}
            {view.comparedTerms.length > 0 && <p className="text-xs text-gray-500">{`Compared: ${view.comparedTerms.join(', ')}`}</p>}
            {view.others.length > 0 && !picking && (
              <button type="button" className={LINK} disabled={busy} onClick={() => setPicking(true)}>Pick another</button>
            )}
          </div>
        )}
        {view && picking && (
          <div className="flex flex-wrap items-center gap-2">
            <select className="rounded border border-gray-300 px-2 py-1 text-sm" value={pickId} onChange={(e) => setPickId(e.target.value)}>
              <option value="">Pick the earlier version</option>
              {view.others.map((o) => <option key={o.filingId} value={o.filingId}>{o.label}</option>)}
            </select>
            <button type="button" className={LINK} disabled={busy || !pickId}
              onClick={() => tap(() => api.compareDocumentWith(filingId, pickId), failed('Could not load the comparison.'))}>
              Compare
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
